import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, output, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '../../elements/theme';
import type { ElementLabelConfig, LabelStyleKey } from '../../elements/label';
import {
  LABEL_POSITIONS,
  LABEL_VARIANTS,
  LABEL_CONTENT_VARIANTS,
  LABEL_TEXT_ALIGNS,
  LABEL_FIELD_CONFIG,
} from '../../elements/label';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Editor for ElementLabelConfig — show/hide, variant, color, position,
 * contentVariant, text, fontSize, precision, anchorAt, textAlign, popup, offset.
 *
 * When `styleKey` is provided, the editor filters options and fields to only
 * show those valid for the given element type (e.g. no position for angles,
 * no anchorAt for points, only relevant content variants).
 */
@Component({
  selector: 'dojo-label-editor',
  standalone: true,
  imports: [ReactiveFormsModule, MatSlideToggleModule, NumberInputComponent, TextInputComponent, SingleSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-1.5">
      <mat-slide-toggle [checked]="isShown()" (change)="onToggleShow($event.checked)">Show Label</mat-slide-toggle>

      @if (isShown()) {
        <div class="grid grid-cols-2 gap-1.5">
          @if (showPosition()) {
            <dojo-single-select
              label="Position"
              [formCtrl]="positionCtrl"
              [options]="positionOptions()"
              optionLabel="label"
              optionValue="value"
              [size]="'s'"
              (selectionChange)="onFieldChange('position', $event)"
            />
          }
          <dojo-single-select
            label="Variant"
            [formCtrl]="variantCtrl"
            [options]="variantOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            (selectionChange)="onFieldChange('variant', $event)"
          />
          <dojo-single-select
            label="Content"
            [formCtrl]="contentVariantCtrl"
            [options]="contentVariantOptions()"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            placeholder="Default"
            (selectionChange)="onFieldChange('contentVariant', $event)"
          />
          @if (showAnchorAt()) {
            <dojo-single-select
              label="Anchor At"
              [formCtrl]="anchorAtCtrl"
              [options]="anchorAtOptions"
              optionLabel="label"
              optionValue="value"
              [size]="'s'"
              placeholder="Middle"
              (selectionChange)="onAnchorAtChange($event)"
            />
          }
          @if (showPlacement()) {
            <dojo-single-select
              label="Placement"
              [formCtrl]="placementCtrl"
              [options]="placementOptions"
              optionLabel="label"
              optionValue="value"
              [size]="'s'"
              placeholder="Inside"
              (selectionChange)="onFieldChange('placement', $event)"
            />
          }
        </div>

        <dojo-text-input label="Custom Text" [formCtrl]="textCtrl" [size]="'s'" placeholder="auto" />

        <div class="grid grid-cols-2 gap-1.5">
          <dojo-number-input label="Font Size" [formCtrl]="fontSizeCtrl" [step]="1" [min]="6" [max]="48" [size]="'s'" />
          <dojo-single-select
            label="Align"
            [formCtrl]="textAlignCtrl"
            [options]="textAlignOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            (selectionChange)="onFieldChange('textAlign', $event)"
          />
          <dojo-number-input
            label="Offset X"
            [formCtrl]="offsetXCtrl"
            [step]="1"
            [min]="-100"
            [max]="100"
            [size]="'s'"
          />
          <dojo-number-input
            label="Offset Y"
            [formCtrl]="offsetYCtrl"
            [step]="1"
            [min]="-100"
            [max]="100"
            [size]="'s'"
          />
          <dojo-number-input
            label="Precision"
            [formCtrl]="precisionCtrl"
            [step]="1"
            [min]="0"
            [max]="10"
            [size]="'s'"
          />
          <dojo-single-select
            label="Color"
            [formCtrl]="colorCtrl"
            [options]="colorOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            placeholder="Default"
            (selectionChange)="onFieldChange('color', $event)"
          />
        </div>

        <mat-slide-toggle [checked]="isPopup()" (change)="onFieldChange('popup', $event.checked)"
          >Popup</mat-slide-toggle
        >
      }
    </div>
  `,
})
export class LabelEditorComponent implements OnChanges {
  constructor(private readonly destroyRef: DestroyRef) {}

  readonly labelConfig = input<ElementLabelConfig | boolean | undefined>();
  readonly labelConfigChange = output<ElementLabelConfig | undefined>();

  /** When provided, filters options and fields to only those valid for this element type. */
  readonly styleKey = input<LabelStyleKey | undefined>();

  // --- Field config computed from styleKey ---
  private readonly fieldConfig = computed(() => {
    const key = this.styleKey();
    return key ? LABEL_FIELD_CONFIG[key] : null;
  });

  readonly showPosition = computed(() => {
    const cfg = this.fieldConfig();
    return cfg ? cfg.positions !== null : true;
  });

  readonly showAnchorAt = computed(() => {
    const cfg = this.fieldConfig();
    return cfg ? cfg.hasAnchorAt : true;
  });

  readonly showPlacement = computed(() => {
    const cfg = this.fieldConfig();
    return cfg ? cfg.hasPlacement : false;
  });

  // --- Option arrays (filtered by styleKey) ---
  readonly positionOptions = computed<SelectOption[]>(() => {
    const cfg = this.fieldConfig();
    const positions = cfg?.positions ?? LABEL_POSITIONS;
    return positions.map((p) => ({ label: p, value: p }));
  });

  readonly contentVariantOptions = computed<SelectOption[]>(() => {
    const cfg = this.fieldConfig();
    const variants = cfg?.contentVariants ?? LABEL_CONTENT_VARIANTS;
    return [{ label: 'Default', value: '' }, ...variants.map((c) => ({ label: c, value: c }))];
  });

  // --- Static option arrays ---
  readonly variantOptions: SelectOption[] = LABEL_VARIANTS.map((v) => ({ label: v, value: v }));
  readonly textAlignOptions: SelectOption[] = LABEL_TEXT_ALIGNS.map((a) => ({ label: a, value: a }));
  readonly anchorAtOptions: SelectOption[] = [
    { label: 'Start', value: 'start' },
    { label: 'Middle', value: 'middle' },
    { label: 'End', value: 'end' },
  ];
  readonly placementOptions: SelectOption[] = [
    { label: 'Inside', value: 'inside' },
    { label: 'Outside', value: 'outside' },
  ];
  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  // --- Form controls ---
  readonly positionCtrl = new FormControl<string>('', { nonNullable: true });
  readonly variantCtrl = new FormControl<string>('', { nonNullable: true });
  readonly contentVariantCtrl = new FormControl<string>('', { nonNullable: true });
  readonly anchorAtCtrl = new FormControl<string>('', { nonNullable: true });
  readonly placementCtrl = new FormControl<string>('', { nonNullable: true });
  readonly textCtrl = new FormControl<string>('', { nonNullable: true });
  readonly textAlignCtrl = new FormControl<string>('', { nonNullable: true });
  readonly colorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly fontSizeCtrl = new FormControl<number | null>(null);
  readonly precisionCtrl = new FormControl<number | null>(null);
  readonly offsetXCtrl = new FormControl<number | null>(null);
  readonly offsetYCtrl = new FormControl<number | null>(null);

  private subsInitialized = false;

  isShown(): boolean {
    const cfg = this.labelConfig();
    if (cfg === true) {
      return true;
    }
    if (cfg === false || cfg === undefined) {
      return false;
    }
    return cfg.visible !== false;
  }

  isPopup(): boolean {
    const cfg = this.labelConfig();
    return typeof cfg === 'object' && !!cfg?.popup;
  }

  ngOnChanges(): void {
    this.syncFromInput();
    if (!this.subsInitialized) {
      this.initSubscriptions();
      this.subsInitialized = true;
    }
  }

  onToggleShow(show: boolean): void {
    if (!show) {
      this.labelConfigChange.emit(undefined);
    } else {
      const current = this.resolveConfig();
      this.labelConfigChange.emit({ ...current, visible: true });
    }
  }

  onFieldChange(field: string, value: unknown): void {
    const current = this.resolveConfig();
    const cleanValue = value === '' ? undefined : value;
    this.labelConfigChange.emit({ ...current, [field]: cleanValue });
  }

  onAnchorAtChange(value: unknown): void {
    const current = this.resolveConfig();
    this.labelConfigChange.emit({ ...current, anchorAt: (value as ElementLabelConfig['anchorAt']) || undefined });
  }

  private resolveConfig(): ElementLabelConfig {
    const cfg = this.labelConfig();
    if (cfg === true) {
      return { visible: true };
    }
    if (cfg === false || cfg === undefined) {
      return { visible: false };
    }
    return { ...cfg };
  }

  private syncFromInput(): void {
    const cfg = this.labelConfig();
    const resolved = typeof cfg === 'object' && cfg ? cfg : ({} as ElementLabelConfig);

    this.positionCtrl.setValue((resolved.position as string) ?? '', { emitEvent: false });
    this.variantCtrl.setValue((resolved.variant as string) ?? '', { emitEvent: false });
    this.contentVariantCtrl.setValue((resolved.contentVariant as string) ?? '', { emitEvent: false });
    this.anchorAtCtrl.setValue(resolved.anchorAt !== null ? String(resolved.anchorAt) : '', { emitEvent: false });
    this.placementCtrl.setValue((resolved.placement as string) ?? '', { emitEvent: false });
    this.textCtrl.setValue(resolved.text ?? '', { emitEvent: false });
    this.textAlignCtrl.setValue((resolved.textAlign as string) ?? '', { emitEvent: false });
    this.colorCtrl.setValue((resolved.color as string) ?? '', { emitEvent: false });
    this.fontSizeCtrl.setValue(resolved.fontSize ?? null, { emitEvent: false });
    this.precisionCtrl.setValue(resolved.precision ?? null, { emitEvent: false });
    this.offsetXCtrl.setValue(resolved.offset?.[0] ?? null, { emitEvent: false });
    this.offsetYCtrl.setValue(resolved.offset?.[1] ?? null, { emitEvent: false });
  }

  private initSubscriptions(): void {
    this.textCtrl.valueChanges
      .pipe(
        tap((val) => this.onFieldChange('text', val || undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.fontSizeCtrl.valueChanges
      .pipe(
        tap((val) => this.onFieldChange('fontSize', val)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.precisionCtrl.valueChanges
      .pipe(
        tap((val) => this.onFieldChange('precision', val)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.offsetXCtrl.valueChanges
      .pipe(
        tap((val) => {
          const current = this.resolveConfig();
          const offset: [number, number] = [val ?? 0, current.offset?.[1] ?? 0];
          this.labelConfigChange.emit({ ...current, offset });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.offsetYCtrl.valueChanges
      .pipe(
        tap((val) => {
          const current = this.resolveConfig();
          const offset: [number, number] = [current.offset?.[0] ?? 0, val ?? 0];
          this.labelConfigChange.emit({ ...current, offset });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
